<script setup>
/**
 * Four labelled boxes instead of one raw document — and the escape hatch that makes that
 * safe. THE PLAN, §4 P3.
 *
 * The old screen handed an owner a single empty textarea and a 20,000-character limit and
 * called it a persona. Four questions she can answer — who it is, what the business does,
 * what it must always do, what it must never do — is the same document, asked in a way a
 * shop owner in Salmiya can finish in five minutes without knowing what a prompt is.
 *
 * WHAT MAKES IT NOT A DATA-LOSS BUG: some accounts already have a document somebody wrote
 * by hand, and it will not round-trip into four boxes. Those open in a SINGLE-DOCUMENT
 * editor showing the text exactly as stored, behind a banner that OFFERS the conversion
 * and never performs it. The conversion itself goes through the same preview-then-accept
 * assist as everything else, so at no point is a hand-written document rewritten by a
 * model without the owner reading the result first and pressing a button. This is a hard
 * requirement of the piece, not a nicety — see `helpers/prompt-sections.js`, which is
 * strict on the read path for exactly this reason.
 */
import { computed, ref, watch } from 'vue';

import Banner from 'dashboard/components-next/banner/Banner.vue';

import FirstDraftLink from './FirstDraftLink.vue';
import OptimizeField from './OptimizeField.vue';
import { useAiConfirm } from '../composables/useAiConfirm.js';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloI18n } from '../i18n.js';
import {
  SECTION_KEYS,
  composeSections,
  parseLoosely,
  parseSections,
} from '../helpers/prompt-sections.js';

const props = defineProps({
  /** The document as stored. Empty string is a valid, ordinary first-run value. */
  modelValue: { type: String, default: '' },
  /**
   * True while the account still carries the shipped default. There is nothing of the
   * owner's to preserve, so first run gets the four empty boxes rather than a wall of
   * text she did not write and would have to read before she could start.
   */
  isDefault: { type: Boolean, default: false },
  /** Validation message for the whole editor, e.g. "tell it who it is first". */
  message: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useWasloI18n();

// The banner starts the same paid Suggest as the link in the box below it (P-5.12), so it
// wears the same price. The confirm itself is the box's own (`requestSuggestion`).
const bannerPrice = useAiConfirm({
  accountId: useWasloAccountId(),
  op: 'suggest_wording',
});
const bannerActionLabel = computed(() =>
  bannerPrice.label(t('WASLO.PERSONALITY.DOCUMENT.BANNER_ACTION'))
);

const MODE = { SECTIONS: 'sections', DOCUMENT: 'document' };

const mode = ref(MODE.SECTIONS);
const sections = ref({ identity: '', business: '', always: '', never: '' });
const documentText = ref('');
const documentField = ref(null);
const previewSplits = ref(false);

// What this component last pushed upward. The parent re-feeds `modelValue` after a save,
// and rehydrating on our own echo would throw away an in-progress edit.
let lastEmitted = null;

const hydrate = value => {
  const document = value ?? '';
  documentText.value = document;

  if (props.isDefault || !document.trim()) {
    mode.value = MODE.SECTIONS;
    sections.value = { identity: '', business: '', always: '', never: '' };
    return;
  }

  const parsed = parseSections(document);
  if (parsed) {
    mode.value = MODE.SECTIONS;
    sections.value = parsed;
    return;
  }

  mode.value = MODE.DOCUMENT;
  sections.value = { identity: '', business: '', always: '', never: '' };
};

hydrate(props.modelValue);

watch(
  () => props.modelValue,
  value => {
    if (value === lastEmitted) return;
    hydrate(value);
  }
);

const publish = () => {
  const next =
    mode.value === MODE.SECTIONS
      ? composeSections(sections.value)
      : documentText.value;
  lastEmitted = next;
  emit('update:modelValue', next);
};

const setSection = (key, value) => {
  sections.value = { ...sections.value, [key]: value };
  publish();
};

const setDocument = value => {
  documentText.value = value;
  publish();
};

const fields = computed(() =>
  SECTION_KEYS.map(key => ({
    key,
    label: t(`WASLO.PERSONALITY.FIELDS.${key.toUpperCase()}.LABEL`),
    placeholder: t(`WASLO.PERSONALITY.FIELDS.${key.toUpperCase()}.PLACEHOLDER`),
    hint: t(`WASLO.PERSONALITY.FIELDS.${key.toUpperCase()}.HINT`),
  }))
);

const startConversion = () => documentField.value?.requestSuggestion();

// The preview panel says which of the two things accepting will do, before it happens.
const onSuggestion = text => {
  previewSplits.value = Boolean(text) && parseLoosely(text) !== null;
};

const previewNote = computed(() =>
  previewSplits.value
    ? t('WASLO.PERSONALITY.DOCUMENT.PREVIEW_SPLITS')
    : t('WASLO.PERSONALITY.DOCUMENT.PREVIEW_NO_SPLIT')
);

const onSuggestionAccepted = accepted => {
  const split = parseLoosely(accepted);
  if (!split) return; // Stays one document; the accepted text is already in the field.
  sections.value = split;
  mode.value = MODE.SECTIONS;
  publish();
};
</script>

<template>
  <!--
    gap-8 between questions against ≤8px inside each one. The dark critic's exact words:
    "the gap from that row to the next label is smaller than the gap inside the group — so
    label / field / hint never bind into one unit and the four questions run together into
    a wall". Roughly four-to-one is what makes four questions look like four questions.
  -->
  <div class="flex flex-col gap-8" data-waslo-prompt-editor :data-mode="mode">
    <template v-if="mode === MODE.SECTIONS">
      <!-- P-8.1: while it is still Waslo's default, a first draft from what Meta already holds. -->
      <FirstDraftLink v-if="isDefault" />
      <OptimizeField
        v-for="(field, index) in fields"
        :key="field.key"
        :model-value="sections[field.key]"
        :label="field.label"
        :placeholder="field.placeholder"
        :hint="field.hint"
        :message="index === 0 ? message : ''"
        :autofocus="index === 0"
        @update:model-value="setSection(field.key, $event)"
      />
    </template>

    <template v-else>
      <Banner
        color="slate"
        :action-label="bannerActionLabel"
        @action="startConversion"
      >
        {{ t('WASLO.PERSONALITY.DOCUMENT.BANNER') }}
      </Banner>

      <OptimizeField
        ref="documentField"
        :model-value="documentText"
        :label="t('WASLO.PERSONALITY.DOCUMENT.LABEL')"
        :placeholder="t('WASLO.PERSONALITY.DOCUMENT.PLACEHOLDER')"
        :hint="t('WASLO.PERSONALITY.DOCUMENT.HINT')"
        :message="message"
        :preview-note="previewNote"
        min-height="9rem"
        @update:model-value="setDocument"
        @suggestion="onSuggestion"
        @accepted="onSuggestionAccepted"
      />
    </template>
  </div>
</template>
